import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, CalendarDays, Phone, Car, Clock, Users, Dog, Loader2, MessageSquare, ChevronRight, Mail, CreditCard, CircleDollarSign, Check, Banknote, Building2, MoreHorizontal, Send, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DepositControls from "@/components/admin/DepositControls";
import CheckInLinkCell from "@/components/admin/CheckInLinkCell";
import CheckInStatusBadge from "@/components/admin/CheckInStatusBadge";
import MoveBookingDialog from "@/components/admin/MoveBookingDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isLodge = (b) => b.unit_type === "lodge" || (b.unit_id || "").startsWith("lodge-");

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
};
const fmt12 = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${suffix}`;
};

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

/**
 * Work out how much the guest has paid and how much they still owe on arrival.
 *
 *   payment_status = "paid"    + payment_type = "full"    → fully paid
 *   payment_status = "paid"    + payment_type = "deposit" → deposit paid, balance owed
 *   payment_status = "pending"                            → nothing paid yet
 */
const paymentBreakdown = (b) => {
  const total = Number(b.total || 0);
  const deposit = Number(b.deposit || 0);
  if ((b.source || "").startsWith("external:")) {
    const channel = b.source.split(":")[1] || "channel";
    return { total, paid: 0, balance: 0, label: `Paid via ${channel}`, external: true };
  }
  // What was genuinely collected online — frozen when the original payment
  // completed. Legacy bookings predate the field: estimate from the
  // pre-modification total (total minus any extension uplift) so an extended
  // stay shows the difference as owed, not as "fully paid".
  const uplift = Number(b.price_uplift || 0);
  const baseTotal = Math.max(0, total - uplift);
  const manual = Number(b.balance_paid_amount || 0);
  if (b.payment_status === "paid" && b.payment_type === "full") {
    const online = Number(b.online_collected || 0) || baseTotal;
    const paid = online + manual;
    return { total, paid, balance: Math.max(0, total - paid), label: paid >= total ? "Paid in full" : "Part paid" };
  }
  if (b.payment_status === "paid" && b.payment_type === "deposit") {
    const online = Number(b.online_collected || 0) || Math.min(deposit, Math.round(baseTotal * 25) / 100);
    const settled = online + manual;
    return { total, paid: settled, balance: Math.max(0, total - settled), label: "Deposit paid" };
  }
  return { total, paid: 0, balance: total, label: "Awaiting payment" };
};

/** Daily arrivals brief — printable, mobile-friendly, private. */
const AdminArrivals = () => {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settlingId, setSettlingId] = useState(null);      // booking id showing the method picker
  const [savingId, setSavingId] = useState(null);          // booking id currently being saved
  const [checkInId, setCheckInId] = useState(null);        // booking id currently being checked in/out
  const [modifyingId, setModifyingId] = useState(null);    // booking id with the modify editor open
  const [modForm, setModForm] = useState({ start_date: "", end_date: "", guests: 2, dogs: 0 });
  const [modBusy, setModBusy] = useState(false);
  const [linkBusyId, setLinkBusyId] = useState(null);      // booking id currently sending a payment link
  const [note, setNote] = useState("");

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/arrivals`, { params: { date: d } });
      setData(data);
    } catch {
      toast.error("Failed to load arrivals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); }, [date]);

  const settle = async (booking, method) => {
    setSavingId(booking.id);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/bookings/${booking.id}/mark-balance-paid`,
        { method, note: note || null },
      );
      toast.success(`Marked as paid · ${gbp(res.collected || 0)} ${method}`);
      setSettlingId(null);
      setNote("");
      await load(date);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't mark as paid.");
    } finally {
      setSavingId(null);
    }
  };

  // Mark a guest as arrived (or undo back to waiting)
  const toggleCheckIn = async (booking, checkedIn) => {
    setCheckInId(booking.id);
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/check-in`, { checked_in: checkedIn });
      toast.success(checkedIn ? `${booking.guest_name || "Guest"} marked as arrived.` : "Back to waiting on arrival.");
      await load(date);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update check-in.");
    } finally {
      setCheckInId(null);
    }
  };

  // ---- modify / extend a stay, then collect the difference by payment link ----
  const openModify = (b) => {
    setModifyingId(modifyingId === b.id ? null : b.id);
    setModForm({ start_date: b.start_date, end_date: b.end_date, guests: b.guests || 2, dogs: b.dogs || 0 });
  };

  const saveModify = async (b) => {
    setModBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${b.id}/modify`, {
        start_date: modForm.start_date,
        end_date: modForm.end_date,
        guests: Number(modForm.guests) || b.guests,
        ...(isLodge(b) ? { dogs: Number(modForm.dogs) || 0 } : {}),
      });
      const delta = Number(data.difference || 0);
      toast.success(
        `Stay updated — new total ${gbp(data.new_total)}` +
        (delta > 0 ? ` · ${gbp(delta)} more to collect — send the payment link below.` : delta < 0 ? ` · ${gbp(-delta)} less.` : " · no price change.")
      );
      setModifyingId(null);
      await load(date);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update the stay.");
    } finally {
      setModBusy(false);
    }
  };

  // Collect whatever is outstanding (extension balance, deposit, full stay) — the
  // backend always works out the correct amount and emails the guest.
  const sendPaymentLink = async (b) => {
    setLinkBusyId(b.id);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${b.id}/payment-link`);
      if (data.checkout_url) { try { await navigator.clipboard.writeText(data.checkout_url); } catch {} }
      toast.success(data.emailed
        ? `Payment link for ${gbp(data.amount)} emailed to the guest — copied too.`
        : `Payment link ready for ${gbp(data.amount)} — copied to clipboard.`);
      await load(date);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create the payment link.");
    } finally {
      setLinkBusyId(null);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-arrivals-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="overline text-[#B76E79] flex items-center gap-2"><span className="marker-line marker-line--rose" /> On-the-ground</p>
          <h3 className="font-serif text-2xl md:text-3xl mt-1">Arrivals brief</h3>
          <p className="text-sm text-[#6A5F58] mt-1">Everyone checking in on the selected day — sorted by arrival time.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-2 h-11 rounded-lg w-44"
              data-testid="arrivals-date"
            />
          </div>
          <button
            type="button"
            onClick={() => setDate(today())}
            className="h-11 px-3 rounded-lg border border-[#DDD1BE] text-xs uppercase tracking-[0.14em] hover:bg-[#F5EFE6]"
            data-testid="arrivals-today"
          >Today</button>
          <button
            type="button"
            onClick={() => setDate(addDays(today(), 1))}
            className="h-11 px-3 rounded-lg border border-[#DDD1BE] text-xs uppercase tracking-[0.14em] hover:bg-[#F5EFE6]"
            data-testid="arrivals-tomorrow"
          >Tomorrow</button>
          <button
            type="button"
            onClick={() => window.print()}
            className="h-11 px-3 rounded-lg border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-xs uppercase tracking-[0.14em]"
          >Print</button>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}

      {!loading && data && data.count === 0 && (data.departures || []).length === 0 && (
        <div className="text-center py-10 text-[#6A5F58]" data-testid="arrivals-empty">
          <Calendar size={28} className="mx-auto mb-3 opacity-40" />
          No arrivals or departures on {date}.
        </div>
      )}

      {!loading && data && data.count > 0 && (
        <div className="space-y-3" data-testid="arrivals-list">
          {(() => {
            const totals = data.bookings.reduce((acc, b) => {
              const p = paymentBreakdown(b);
              acc.balance += p.balance;
              acc.paid += p.paid;
              return acc;
            }, { balance: 0, paid: 0 });
            return (
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                <span>{data.count} arrival{data.count > 1 ? "s" : ""} · {date}</span>
                {(() => {
                  const done = data.bookings.filter((b) => !!b.checkin_form).length;
                  const missing = data.count - done;
                  const due = date <= today();
                  return (
                    <span className="inline-flex items-center gap-2" data-testid="arrivals-checkin-tally">
                      <span className="inline-flex items-center gap-1 text-[#3B8C4E] font-semibold"><span className="w-2 h-2 rounded-full bg-[#3B8C4E] inline-block" /> {done} checked in</span>
                      {missing > 0 && (
                        <span className={`inline-flex items-center gap-1 font-semibold ${due ? "text-[#B3394F]" : "text-[#C89B5F]"}`} data-testid="arrivals-checkin-missing">
                          <span className={`w-2 h-2 rounded-full inline-block ${due ? "bg-[#B3394F]" : "bg-[#C89B5F]"}`} /> {missing} {due ? "not checked in" : "pending"}
                        </span>
                      )}
                    </span>
                  );
                })()}
                {totals.balance > 0 && (
                  <span className="text-[#B76E79] font-semibold" data-testid="arrivals-balance-total">
                    Collect on arrival: {gbp(totals.balance)}
                  </span>
                )}
              </div>
            );
          })()}
          {data.bookings.map((b) => {
            const pay = paymentBreakdown(b);
            return (
            <div key={b.id} className="rounded-xl border border-[#DDD1BE] bg-white p-4 md:p-5 shadow-sm" data-testid={`arrival-${b.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Guest</p>
                  <p className="font-serif text-lg text-[#14100F] mt-0.5">{b.guest_name || "—"}</p>
                  <a href={`tel:${(b.guest_phone || "").replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-1 text-sm text-[#0F0E0E] hover:text-[#B76E79]" data-testid={`arrival-phone-${b.id}`}>
                    <Phone size={12} /> {b.guest_phone || "no phone"}
                  </a>
                  {b.guest_email && (
                    <a href={`mailto:${b.guest_email}`} className="mt-0.5 flex items-center gap-1 text-[11px] text-[#6A5F58] hover:text-[#B76E79] break-all" data-testid={`arrival-email-${b.id}`}>
                      <Mail size={11} /> {b.guest_email}
                    </a>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Pitch / lodge</p>
                  <p className="font-serif text-lg text-[#14100F] mt-0.5">{b.unit_id}</p>
                  {b.sub_type && <p className="text-[11px] text-[#6A5F58] uppercase tracking-[0.14em] mt-0.5">{b.sub_type} category</p>}
                  <p className="text-[11px] text-[#6A5F58] mt-0.5">{b.start_date} → {b.end_date} · {b.nights} night{b.nights > 1 ? "s" : ""}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Details</p>
                  <div className="mt-1 space-y-1 text-sm">
                    <p className="flex items-center gap-1"><Clock size={13} className="text-[#6A5F58]" /> in {fmt12(b.checkin_time || (isLodge(b) ? "14:00" : "13:00"))} · out {fmt12(b.checkout_time || "11:00")}</p>
                    <p className="flex items-center gap-1"><Users size={13} className="text-[#6A5F58]" /> {b.guests} guest{b.guests > 1 ? "s" : ""}
                      {b.dogs > 0 && <><Dog size={13} className="text-[#6A5F58] ml-2" /> {b.dogs} dog{b.dogs > 1 ? "s" : ""}</>}
                    </p>
                    {b.vehicle_reg && (
                      <p className="flex items-center gap-1 font-mono text-xs"><Car size={13} className="text-[#6A5F58]" /> <span className="bg-[#F5EFE6] px-1.5 py-0.5 rounded">{b.vehicle_reg}</span></p>
                    )}
                  </div>
                </div>
                <div data-testid={`arrival-payment-${b.id}`}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Payment</p>
                  {pay.external ? (
                    <p className="mt-1 text-sm text-[#5B7A8C] font-medium flex items-center gap-1.5">
                      <Check size={13} /> {pay.label}
                    </p>
                  ) : (
                  <div className="mt-1 space-y-0.5 text-sm">
                    <p className="flex items-center justify-between gap-2">
                      <span className="text-[#6A5F58] text-[11px] uppercase tracking-[0.14em]">Total</span>
                      <span className="font-mono text-[#14100F]">{gbp(pay.total)}</span>
                    </p>
                    <p className="flex items-center justify-between gap-2">
                      <span className="text-[#3B8C4E] text-[11px] uppercase tracking-[0.14em] flex items-center gap-1"><CreditCard size={11} /> Paid</span>
                      <span className="font-mono text-[#3B8C4E]">{gbp(pay.paid)}</span>
                    </p>
                    <p className="flex items-center justify-between gap-2 pt-1 border-t border-[#EDE3D2] mt-1">
                      <span className={`text-[11px] uppercase tracking-[0.14em] flex items-center gap-1 font-semibold ${pay.balance > 0 ? "text-[#B76E79]" : "text-[#6A5F58]"}`}>
                        <CircleDollarSign size={11} /> {pay.balance > 0 ? "Due on arrival" : "Balance"}
                      </span>
                      <span className={`font-mono font-semibold ${pay.balance > 0 ? "text-[#B76E79]" : "text-[#6A5F58]"}`}>{gbp(pay.balance)}</span>
                    </p>
                  </div>
                  )}

                  {/* Mark balance paid — collect cash/card at the pitch */}
                  {pay.balance > 0 && settlingId !== b.id && (
                    <button
                      type="button"
                      onClick={() => { setSettlingId(b.id); setNote(""); }}
                      data-testid={`arrival-mark-paid-${b.id}`}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.14em] uppercase"
                    >
                      <Check size={12} /> Mark balance paid
                    </button>
                  )}
                  {settlingId === b.id && (
                    <div className="mt-3 space-y-2 border border-[#B76E79] rounded-lg p-2.5 bg-[#FFF7F3]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">How was it paid?</span>
                        <button type="button" onClick={() => { setSettlingId(null); setNote(""); }} className="text-[#6A5F58] hover:text-[#0F0E0E]" aria-label="Cancel"><X size={12} /></button>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[
                          { id: "cash", icon: Banknote, label: "Cash" },
                          { id: "card", icon: CreditCard, label: "Card" },
                          { id: "transfer", icon: Building2, label: "Bank" },
                        ].map((m) => {
                          const Icon = m.icon;
                          const busy = savingId === b.id;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => settle(b, m.id)}
                              disabled={busy}
                              data-testid={`arrival-method-${m.id}-${b.id}`}
                              className="inline-flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-md bg-white border border-[#DDD1BE] hover:border-[#B76E79] hover:text-[#B76E79] text-[10px] font-brand tracking-[0.08em] uppercase disabled:opacity-50"
                            >
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note (e.g. receipt no.)"
                        className="w-full text-[11px] px-2 py-1.5 rounded border border-[#DDD1BE] bg-white"
                        maxLength={200}
                      />
                    </div>
                  )}

                  {/* Show how the balance was collected */}
                  {pay.balance === 0 && b.balance_paid_method && (
                    <p className="mt-2 text-[10px] text-[#3B8C4E] uppercase tracking-[0.14em]">
                      ✓ Balance collected · {b.balance_paid_method}
                      {b.balance_paid_note ? ` · ${b.balance_paid_note}` : ""}
                    </p>
                  )}

                  {/* Collect the balance by link (e.g. after extending the stay) */}
                  {!pay.external && pay.balance > 0 && (
                    <button
                      type="button"
                      onClick={() => sendPaymentLink(b)}
                      disabled={linkBusyId === b.id}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#B76E79] text-white hover:bg-[#0F0E0E] transition text-[11px] font-brand tracking-[0.14em] uppercase disabled:opacity-50"
                      data-testid={`arrival-sendlink-${b.id}`}
                    >
                      {linkBusyId === b.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Send payment link · {gbp(pay.balance)}
                    </button>
                  )}

                  {/* Modify / extend the stay — dates & guests, then charge the difference */}
                  {modifyingId === b.id ? (
                    <div className="mt-3 border border-[#DDD1BE] rounded-xl p-3 bg-[#FBF7F1]" data-testid={`modify-editor-${b.id}`}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Modify / extend stay</p>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">Arrival</Label>
                          <Input type="date" value={modForm.start_date} onChange={(e) => setModForm({ ...modForm, start_date: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`modify-start-${b.id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">Departure</Label>
                          <Input type="date" value={modForm.end_date} onChange={(e) => setModForm({ ...modForm, end_date: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`modify-end-${b.id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">Guests</Label>
                          <Input type="number" min={1} max={4} value={modForm.guests} onChange={(e) => setModForm({ ...modForm, guests: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`modify-guests-${b.id}`} />
                        </div>
                        {isLodge(b) && (
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">Dogs</Label>
                            <Input type="number" min={0} max={2} value={modForm.dogs} onChange={(e) => setModForm({ ...modForm, dogs: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`modify-dogs-${b.id}`} />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => saveModify(b)}
                          disabled={modBusy}
                          className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#3B8C4E] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
                          data-testid={`modify-save-${b.id}`}
                        >
                          {modBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Save changes
                        </button>
                        <button
                          type="button"
                          onClick={() => setModifyingId(null)}
                          className="px-4 h-10 rounded-full border border-[#DDD1BE] text-[10px] font-brand tracking-[0.12em] uppercase text-[#6A5F58] hover:bg-[#F5EFE6]"
                          data-testid={`modify-cancel-${b.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                      <p className="text-[10px] text-[#6A5F58] mt-2 leading-relaxed">
                        Extending? Save first — the new balance appears above, then tap "Send payment link" to charge it.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openModify(b)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-white transition text-[11px] font-brand tracking-[0.14em] uppercase"
                        data-testid={`modify-open-${b.id}`}
                      >
                        <CalendarDays size={12} /> Modify / extend stay
                      </button>
                      <MoveBookingDialog booking={b} onMoved={() => load(date)} variant="button" />
                    </div>
                  )}

                  {/* £100 refundable lodge security deposit (Square card hold) */}
                  {isLodge(b) && (
                    <DepositControls booking={b} mode="arrival" onChanged={() => load(date)} />
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Status</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold ${pay.external ? "bg-[#5B7A8C] text-white" : b.payment_status === "paid" ? "bg-[#3B8C4E] text-white" : "bg-[#C89B5F] text-white"}`} data-testid={`arrival-status-${b.id}`}>
                    {pay.label}
                  </span>
                  {!pay.external && (
                    <p className="mt-1.5 text-[11px] leading-snug max-w-[200px]" data-testid={`arrival-paid-${b.id}`}>
                      {pay.paid > 0 ? (
                        <span className="font-semibold text-[#3B8C4E]">Paid {gbp(pay.paid)} of {gbp(pay.total)}</span>
                      ) : (
                        <span className="font-semibold text-[#B76E79]">Nothing paid yet</span>
                      )}
                      {pay.balance > 0 ? (
                        <span className="text-[#6A5F58]"> · <span className="font-semibold text-[#C89B5F]">{gbp(pay.balance)} to take</span></span>
                      ) : pay.paid > 0 && (
                        <span className="text-[#6A5F58]"> · fully settled — charge nothing</span>
                      )}
                    </p>
                  )}

                  {/* Colour-coded online check-in status — green complete, red missing on-site, amber pending */}
                  <div className="mt-2.5">
                    <CheckInStatusBadge
                      complete={!!b.checkin_form}
                      onSite={!!b.checked_in_at || date <= today()}
                      testid={`arrival-checkin-status-${b.id}`}
                    />
                  </div>

                  {/* Guest check-in — tap "Arrived" when they turn up */}
                  {b.checked_in_at ? (
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold bg-[#3B8C4E] text-white" data-testid={`arrival-checked-in-${b.id}`}>
                        <Check size={11} /> Arrived {new Date(b.checked_in_at).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" })}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleCheckIn(b, false)}
                        disabled={checkInId === b.id}
                        className="text-[10px] text-[#6A5F58] underline decoration-dotted underline-offset-2 hover:text-[#B76E79] disabled:opacity-50"
                        data-testid={`arrival-undo-checkin-${b.id}`}
                      >
                        undo
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold bg-[#DDD1BE] text-[#6A5F58]" data-testid={`arrival-waiting-${b.id}`}>
                        Waiting on arrival
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleCheckIn(b, true)}
                        disabled={checkInId === b.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#3B8C4E] transition text-[10px] font-brand tracking-[0.12em] uppercase disabled:opacity-50"
                        data-testid={`arrival-checkin-${b.id}`}
                      >
                        {checkInId === b.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                        Arrived
                      </button>
                    </div>
                  )}
                  {/* Online self check-in — send the form link before they set off */}
                  <div className="mt-3">
                    <CheckInLinkCell item={b} />
                  </div>
                  {b.special_requests && (
                    <div className="mt-3 text-[11px] text-[#0F0E0E] bg-[#FBF7F1] border-l-2 border-[#B76E79] px-2 py-1 leading-snug">
                      ✎ {b.special_requests}
                    </div>
                  )}
                  {b.admin_note && (
                    <div className="mt-2 text-[11px] text-[#F5EFE6] bg-[#14100F] rounded px-2 py-1 leading-snug flex items-start gap-1">
                      <MessageSquare size={11} className="mt-0.5 flex-shrink-0" /> {b.admin_note}
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Departures — with deposit release / charge for lodge holds */}
      {!loading && data && (data.departures || []).length > 0 && (
        <div className="mt-10" data-testid="departures-section">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] mb-3" data-testid="departures-count">
            {data.departures_count} departure{data.departures_count > 1 ? "s" : ""} · {date}
          </p>
          <div className="space-y-3">
            {data.departures.map((b) => (
              <div key={b.id} className="rounded-xl border border-[#DDD1BE] bg-white p-4 md:p-5 shadow-sm" data-testid={`departure-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg text-[#14100F]">{b.guest_name || "—"}</p>
                    <p className="text-[11px] text-[#6A5F58] mt-0.5 uppercase tracking-[0.14em]">{b.unit_id} · out by {fmt12(b.checkout_time)}</p>
                    {b.guest_phone && (
                      <a href={`tel:${(b.guest_phone || "").replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-1 text-sm text-[#0F0E0E] hover:text-[#B76E79]">
                        <Phone size={12} /> {b.guest_phone}
                      </a>
                    )}
                  </div>
                  {isLodge(b) && (
                    <div className="min-w-[230px]">
                      <DepositControls booking={b} mode="departure" onChanged={() => load(date)} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* On site now — lodge guests mid-stay (deposit renewals live here) */}
      {/* Cancelled — bookings that WOULD have arrived or been on site today,
          so nobody waits for a guest who isn't coming. Refund state + invoice. */}
      {!loading && data && (data.cancelled || []).length > 0 && (
        <div className="mt-10 border-2 border-[#8C2E39]/40 rounded-2xl overflow-hidden" data-testid="arrivals-cancelled">
          <div className="px-4 py-2.5 bg-[#8C2E39]/10 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8C2E39]">
            Cancelled — not expected on park · {data.cancelled.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
            {data.cancelled.map((b) => {
              const refundDue = Math.max(0, (b.cancel_refund_amount || 0) - (b.total_refunded || 0));
              return (
                <div key={b.id} className="bg-[#8C2E39]/[0.05] border border-[#8C2E39]/25 rounded-xl p-3" data-testid={`arrivals-cancelled-${b.id}`}>
                  <p className="text-sm font-medium text-[#14100F]">{b.guest_name || "Guest"}</p>
                  <p className="text-[11px] text-[#6A5F58] mt-0.5 uppercase tracking-[0.14em]">{b.unit_id} · {b.start_date} → {b.end_date}</p>
                  {b.cancel_reason && <p className="text-[11px] text-[#8C2E39] italic mt-1">“{b.cancel_reason}”</p>}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {refundDue > 0 ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#8C2E39] text-white text-[9px] uppercase tracking-[0.1em] font-semibold" data-testid={`arrivals-cancelled-refund-${b.id}`}>
                        Refund £{refundDue.toFixed(2)} due
                      </span>
                    ) : (b.total_refunded || 0) > 0 ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#3B8C4E]/15 text-[#3B8C4E] text-[9px] uppercase tracking-[0.1em] font-semibold">
                        Refunded £{(b.total_refunded || 0).toFixed(2)} ✓
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-[#DDD1BE]/60 text-[#6A5F58] text-[9px] uppercase tracking-[0.1em] font-semibold">
                        Voucher band / no refund
                      </span>
                    )}
                    <a
                      href={`/admin/invoice/${b.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] text-[9px] uppercase tracking-[0.1em] font-semibold"
                      data-testid={`arrivals-cancelled-invoice-${b.id}`}
                    >
                      <FileText size={10} /> Invoice
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && data && (data.in_stay || []).length > 0 && (
        <div className="mt-10" data-testid="onsite-section">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] mb-3" data-testid="onsite-count">
            On site now · {data.in_stay.length} lodge{data.in_stay.length > 1 ? "s" : ""} mid-stay
          </p>
          <div className="space-y-3">
            {data.in_stay.map((b) => (
              <div key={b.id} className="rounded-xl border border-[#DDD1BE] bg-white p-4 md:p-5 shadow-sm" data-testid={`onsite-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg text-[#14100F]">{b.guest_name || "—"}</p>
                    <p className="text-[11px] text-[#6A5F58] mt-0.5 uppercase tracking-[0.14em]">{b.unit_id} · in {b.start_date} · out {b.end_date}</p>
                    <div className="mt-2">
                      <CheckInStatusBadge complete={!!b.checkin_form} onSite testid={`instay-checkin-status-${b.id}`} />
                    </div>
                    {b.guest_phone && (
                      <a href={`tel:${(b.guest_phone || "").replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-1 text-sm text-[#0F0E0E] hover:text-[#B76E79]">
                        <Phone size={12} /> {b.guest_phone}
                      </a>
                    )}
                  </div>
                  <div className="min-w-[230px]">
                    <DepositControls booking={b} mode="arrival" onChanged={() => load(date)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminArrivals;
